import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './pages/LoginPage';
import VerifyPage from './pages/VerifyPage';
import Dashboard from './pages/Dashboard';
import Appointments from './pages/Appointments';
import AppointmentDetails from './pages/AppointmentDetails';
import NewAppointment from './pages/NewAppointment';
import NewTransportationJob from './pages/NewTransportationJob';
import TransportationAuthorization from './pages/TransportationAuthorization';
import Claimants from './pages/Claimants';
import Claims from './pages/Claims';
import ClaimDetails from './pages/ClaimDetails';
import ServiceLocations from './pages/ServiceLocations';
import Activity from './pages/Activity';
import Analytics from './pages/Analytics';
import SupervisorDashboard from './pages/SupervisorDashboard';
import AuthorizationPage from './pages/AuthorizationPage';
import Layout from './components/Layout';
import SearchResults from './pages/SearchResults';
import ProtectedRoute from './components/ProtectedRoute';
import RoutePersistence from './components/RoutePersistence';
import SandboxBanner from './components/SandboxBanner';
import './App.css';

function AppRoutes() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <Router>
      <RoutePersistence />
      <Routes>
        {/* Public routes */}
        <Route 
          path="/auth/login" 
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />} 
        />
        {/* Always allow /auth/verify to process magic link, even if already authenticated */}
        {/* This route must come before the catch-all to ensure it's matched */}
        <Route 
          path="/auth/verify" 
          element={<VerifyPage />} 
        />
        <Route 
          path="/customer/authorize/:jobId" 
          element={<AuthorizationPage />} 
        />
        <Route 
          path="/transportation/authorize/:jobId" 
          element={<TransportationAuthorization />} 
        />
        
        {/* Protected routes */}
        <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="search" element={<SearchResults />} />
          <Route path="appointments" element={<Appointments />} />
          <Route path="appointments/:id" element={<AppointmentDetails />} />
          <Route path="appointments/new" element={<NewAppointment />} />
          <Route path="transportation/new" element={<NewTransportationJob />} />
          <Route path="claimants" element={<Claimants />} />
          <Route path="claims" element={<Claims />} />
          <Route path="claims/:id" element={<ClaimDetails />} />
          <Route path="activity" element={<Activity />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="supervisor" element={<SupervisorDashboard />} />
          <Route path="service-locations" element={<ServiceLocations />} />
        </Route>

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to={isAuthenticated ? "/dashboard" : "/auth/login"} replace />} />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <SandboxBanner />
        <AppRoutes />
        <Toaster position="top-right" />
      </div>
    </AuthProvider>
  );
}

export default App;